/**
 * PrintablePOSummary
 *
 * Clean, single-page-first PO summary used by the PO Detail modal's Print
 * button. Hidden on screen (`hidden print:block`) and revealed by the
 * @media print rule in `index.css`, which hides every other surface so
 * the printer / "Save as PDF" sees only this subtree.
 *
 * Sections:
 *   1. Header     — PO number, status, issue date, customer + supplier
 *   2. Summary    — currency, total value, terms, delivery, ack metadata
 *   3. Lines      — full table with status / qty / value per line
 *   4. Totals     — line count by status + grand total
 *   5. Notes      — free-text notes if present
 *   6. Footer     — generated-on + generated-by stamp
 *
 * Pure presentational. Receives the already-loaded PO object plus the
 * formatters from the parent so currency / dates render in the user's
 * preferred locale.
 */
import type { PurchaseOrder } from '../../lib/api';

type FormatCurrency = (value: string, currency?: string, decimals?: boolean | number) => string;
type FormatDate = (dateString: string) => string;
type FormatDateTime = (dateString: string) => string;

interface Props {
  po: PurchaseOrder;
  formatCurrency: FormatCurrency;
  formatDate: FormatDate;
  formatDateTime: FormatDateTime;
  generatedBy?: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  acknowledged: 'Acknowledged',
  in_progress: 'In Progress',
  delivered: 'Delivered',
  closed: 'Closed',
  pending: 'Pending',
  partially_received: 'Partially Received',
  received: 'Received',
};

const ACK_LABEL: Record<string, string> = {
  new: 'New',
  declined: 'Declined',
  change_proposed_supplier: 'Change Proposed (Supplier)',
  change_proposed_customer: 'Change Proposed (Customer)',
  wip: 'WIP',
  accepted: 'Accepted',
  despatched: 'Despatched',
  change_declined: 'Change Declined',
  cancelled: 'Cancelled',
  closed: 'Closed',
};

export function PrintablePOSummary({
  po,
  formatCurrency,
  formatDate,
  formatDateTime,
  generatedBy,
}: Props) {
  const lines = po.lines ?? [];
  const lineByStatus = lines.reduce<Record<string, number>>((acc, l) => {
    acc[l.status] = (acc[l.status] ?? 0) + 1;
    return acc;
  }, {});

  const cell = 'border border-black px-2 py-1 text-left align-top';
  const cellNum = 'border border-black px-2 py-1 text-right align-top whitespace-nowrap';

  return (
    <div className="printable-po hidden print:block">
      {/* 1. Header */}
      <header className="border-b-2 border-black pb-3 mb-4">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-bold leading-none">Purchase Order</h1>
            <div className="mt-1 text-base">PO #{po.po_number}</div>
          </div>
          <div className="text-right text-xs">
            <div><span className="font-semibold">Status:</span> {STATUS_LABEL[po.status] ?? po.status}</div>
            <div><span className="font-semibold">Issued:</span> {formatDate(po.issue_date)}</div>
            <div><span className="font-semibold">Required by:</span> {po.delivery_date ? formatDate(po.delivery_date) : '—'}</div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-6 text-xs">
          <div>
            <div className="font-semibold uppercase tracking-wider">Customer</div>
            <div className="mt-0.5">{po.customer_name || '—'}</div>
            {po.delivery_warehouse_details?.name && (
              <div className="mt-0.5">Deliver to: {po.delivery_warehouse_details.name}</div>
            )}
          </div>
          <div>
            <div className="font-semibold uppercase tracking-wider">Supplier</div>
            <div className="mt-0.5">{po.supplier_name || '—'}</div>
            {po.supplier_group_name && (
              <div className="mt-0.5">{po.supplier_group_name}</div>
            )}
          </div>
        </div>
      </header>

      {/* 2. Summary */}
      <section className="mb-4 grid grid-cols-4 gap-x-6 gap-y-2 text-xs">
        <div>
          <div className="font-semibold uppercase tracking-wider">Currency</div>
          <div>{po.currency || '—'}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Total Value</div>
          <div>{formatCurrency(po.total_value || '0', po.currency)}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Payment Terms</div>
          <div>{po.payment_terms || '—'}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Shipping Terms</div>
          <div>{po.shipping_terms || '—'}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Created By</div>
          <div>{po.created_by_name || '—'}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Created</div>
          <div>{po.created_at ? formatDateTime(po.created_at) : '—'}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Acknowledged By</div>
          <div>{po.acknowledged_by_name || '—'}</div>
        </div>
        <div>
          <div className="font-semibold uppercase tracking-wider">Acknowledged</div>
          <div>{po.acknowledged_date ? formatDateTime(po.acknowledged_date) : '—'}</div>
        </div>
      </section>

      {/* 3. Lines */}
      <section className="mb-4">
        <h2 className="text-sm font-bold uppercase tracking-wider mb-1">Lines ({lines.length})</h2>
        <table className="w-full text-xs border border-black border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className={cell}>#</th>
              <th className={cell}>Part</th>
              <th className={cell}>Description</th>
              <th className={cellNum}>Qty</th>
              <th className={cellNum}>Recd</th>
              <th className={cellNum}>Outstanding</th>
              <th className={cellNum}>Unit Price</th>
              <th className={cellNum}>Line Total</th>
              <th className={cell}>Delivery</th>
              <th className={cell}>Status</th>
              <th className={cell}>Ack</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td className={cell} colSpan={11}>No line items.</td>
              </tr>
            ) : (
              lines.map((l) => (
                <tr key={l.id}>
                  <td className={cellNum}>{l.line_number}</td>
                  <td className={cell}>{l.part_number || '—'}</td>
                  <td className={cell}>{l.description || '—'}</td>
                  <td className={cellNum}>{Number(l.quantity || 0).toLocaleString()}</td>
                  <td className={cellNum}>{Number(l.received_qty || 0).toLocaleString()}</td>
                  <td className={cellNum}>{Number(l.outstanding_qty || 0).toLocaleString()}</td>
                  <td className={cellNum}>{formatCurrency(l.unit_price || '0', l.currency || po.currency)}</td>
                  <td className={cellNum}>{formatCurrency(l.total_price || '0', l.currency || po.currency)}</td>
                  <td className={cell}>{l.delivery_date ? formatDate(l.delivery_date) : '—'}</td>
                  <td className={cell}>{STATUS_LABEL[l.status] ?? l.status}</td>
                  <td className={cell}>{l.acknowledge_status ? (ACK_LABEL[l.acknowledge_status] ?? l.acknowledge_status) : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      {/* 4. Totals */}
      <section className="mb-4 flex items-end justify-between text-xs">
        <div>
          <div className="font-semibold uppercase tracking-wider">Lines by status</div>
          <div className="mt-0.5">
            {Object.entries(lineByStatus).length === 0
              ? '—'
              : Object.entries(lineByStatus)
                  .map(([s, n]) => `${STATUS_LABEL[s] ?? s}: ${n}`)
                  .join('  •  ')}
          </div>
        </div>
        <div className="text-right">
          <div className="font-semibold uppercase tracking-wider">Grand Total</div>
          <div className="text-lg font-bold">{formatCurrency(po.total_value || '0', po.currency)}</div>
        </div>
      </section>

      {/* 5. Notes */}
      {po.notes && (
        <section className="mb-4 text-xs">
          <h2 className="text-sm font-bold uppercase tracking-wider mb-1">Notes</h2>
          <div className="whitespace-pre-wrap border border-black p-2">{po.notes}</div>
        </section>
      )}

      {/* 6. Footer */}
      <footer className="mt-6 pt-2 border-t border-black text-[10px] text-gray-700 flex justify-between">
        <span>Generated {formatDateTime(new Date().toISOString())}{generatedBy ? ` by ${generatedBy}` : ''}</span>
        <span>PO #{po.po_number}</span>
      </footer>
    </div>
  );
}
